import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Profile } from "@/types/state";

export interface AuthState {
  loading: boolean;
  user: User | null;
  profile: Profile | null;
}

let cached: AuthState = { loading: true, user: null, profile: null };
const listeners = new Set<(s: AuthState) => void>();

function setAuth(next: AuthState) {
  cached = next;
  listeners.forEach((l) => l(next));
}

async function fetchProfile(user: User | null): Promise<Profile | null> {
  if (!user) return null;
  const { data } = await supabase
    .from("profiles")
    .select("id, display_name, role")
    .eq("id", user.id)
    .maybeSingle();
  if (data) return data as Profile;
  // Auto-create profile on first sign-in
  const display_name =
    (user.user_metadata?.display_name as string | undefined) ??
    user.email?.split("@")[0] ??
    "Player";
  const role: "admin" | "player" = "player";
  const { data: created } = await supabase
    .from("profiles")
    .insert({ id: user.id, display_name, role })
    .select()
    .maybeSingle();
  return (created as Profile | null) ?? { id: user.id, display_name, role };
}

let initialized = false;
function ensureInit() {
  if (initialized) return;
  initialized = true;
  supabase.auth.getSession().then(async ({ data }) => {
    const user = data.session?.user ?? null;
    const profile = await fetchProfile(user);
    setAuth({ loading: false, user, profile });
  });
  supabase.auth.onAuthStateChange(async (_evt, session) => {
    const user = session?.user ?? null;
    const profile = await fetchProfile(user);
    setAuth({ loading: false, user, profile });
  });
}

export function useAuth(): AuthState & {
  signIn: (email: string, password: string) => Promise<string | null>;
  signUp: (email: string, password: string, displayName: string) => Promise<string | null>;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>(cached);
  useEffect(() => {
    ensureInit();
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return {
    ...state,
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return error ? error.message : null;
    },
    signUp: async (email, password, display_name) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name } },
      });
      return error ? error.message : null;
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };
}
